/**
 * ADR-0155 §8.3 — the sync-policy card, one shape for all four homes
 * (tenant admin shared / group detail team / Account "Your teams" / Account
 * personal). Target shows display names only; connection reference + health
 * pill; mirror rollup; enable toggle; the "turning off leaves existing
 * copies" note.
 */

import { useCallback, useEffect, useMemo, useState } from "react";
import { apiFetch, ApiRequestError } from "../../lib/api.js";
import { useConfirm } from "../ConfirmDialog.js";
import { useToast } from "../Toast.js";
import { rollupLine, type MirrorState } from "../../lib/mirrorDisplay.js";

interface PolicyResponse {
  policy: {
    scope: string;
    target_kind: "sharepoint" | "onedrive";
    target_root: string;
    target_display: string;
    enabled: boolean;
    updated_at: string;
  } | null;
  rollup?: Partial<Record<MirrorState, number>>;
  connection?: {
    name: string;
    domain: string | null;
    status: "success" | "failed" | null;
    error: string | null;
  };
}

export function SyncPolicyCard(props: {
  title: string;
  /** Hub path (without /api prefix already applied): e.g. "/api/tenant/document-sync/mine". */
  apiPath: string;
  scope: "shared" | "team" | "personal";
  editable: boolean;
  /** Shown when the backing connection is absent (server refuses with not_connected). */
  notConnectedHint: string;
}): JSX.Element {
  const toast = useToast();
  const { confirm } = useConfirm();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<PolicyResponse | null>(null);
  const [targetRoot, setTargetRoot] = useState("");
  const [savedTargetRoot, setSavedTargetRoot] = useState("");
  const [busy, setBusy] = useState(false);
  const [fieldError, setFieldError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiFetch<PolicyResponse>(props.apiPath);
      setData(res);
      const root = res.policy?.target_root ?? "";
      setTargetRoot(root);
      setSavedTargetRoot(root);
    } catch {
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [props.apiPath]);

  useEffect(() => {
    void load();
  }, [load]);

  const dirty = useMemo(() => targetRoot.trim() !== savedTargetRoot, [targetRoot, savedTargetRoot]);

  async function save(enabled?: boolean) {
    setBusy(true);
    setFieldError(null);
    try {
      await apiFetch(props.apiPath, {
        method: "PUT",
        body: { target_root: targetRoot.trim(), enabled: enabled ?? data?.policy?.enabled ?? true }
      });
      toast.push({ kind: "success", message: "Sync policy saved. Documents mirror within a few minutes." });
      await load();
    } catch (e) {
      const message =
        e instanceof ApiRequestError && e.data && typeof (e.data as { message?: unknown }).message === "string"
          ? ((e.data as { message: string }).message)
          : e instanceof Error
            ? e.message
            : String(e);
      if (e instanceof ApiRequestError && e.code === "not_connected") {
        setFieldError(props.notConnectedHint);
      } else {
        setFieldError(message);
      }
    } finally {
      setBusy(false);
    }
  }

  async function toggleEnabled() {
    if (!data?.policy) return;
    const next = !data.policy.enabled;
    if (!next) {
      const ok = await confirm({
        title: "Turn off sync?",
        detail: "New documents stop mirroring. Copies already delivered stay where they are — Atlas never deletes from the mirror.",
        confirmLabel: "Turn off"
      });
      if (!ok) return;
    }
    setBusy(true);
    try {
      await apiFetch(props.apiPath, {
        method: "PUT",
        body: { target_root: data.policy.target_root, enabled: next }
      });
      toast.push({ kind: "success", message: next ? "Sync turned on." : "Sync turned off. Existing copies stay in place." });
      await load();
    } catch (e) {
      toast.push({ kind: "error", message: e instanceof Error ? e.message : String(e) });
    } finally {
      setBusy(false);
    }
  }

  async function remove() {
    const ok = await confirm({
      title: "Remove this sync policy?",
      detail: "Mirroring stops. Copies already delivered stay where they are — Atlas never deletes from the mirror.",
      confirmLabel: "Remove"
    });
    if (!ok) return;
    setBusy(true);
    try {
      await apiFetch(props.apiPath, { method: "DELETE" });
      toast.push({ kind: "success", message: "Sync policy removed. Existing copies stay in place." });
      await load();
    } catch (e) {
      toast.push({ kind: "error", message: e instanceof Error ? e.message : String(e) });
    } finally {
      setBusy(false);
    }
  }

  const placeholder =
    props.scope === "personal" ? "/Deliverables/Atlas" : "https://yourcompany.sharepoint.com/sites/Deliverables";
  const help =
    props.scope === "personal"
      ? "A folder path in your own OneDrive. Atlas creates a folder per study inside it."
      : "The SharePoint site (optionally /library/folder) to mirror into. Atlas creates a folder per study inside it.";

  if (loading) {
    return (
      <div className="card" style={{ display: "grid", gap: 10 }}>
        <div style={{ fontWeight: 700 }}>{props.title}</div>
        <div className="muted">Loading…</div>
      </div>
    );
  }

  const policy = data?.policy ?? null;
  const connection = data?.connection;

  return (
    <div className="card" style={{ display: "grid", gap: 10 }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <div style={{ fontWeight: 700 }}>{props.title}</div>
        {policy ? (
          policy.enabled ? <span className="pill pillOk">On</span> : <span className="pill pillNeutral">Off</span>
        ) : (
          <span className="pill pillNeutral">Not set</span>
        )}
      </div>

      {policy ? (
        <div style={{ display: "grid", gap: 4, fontSize: 13 }}>
          <div>
            <span className="muted">Mirrors to </span>
            {policy.target_display}
          </div>
          {connection ? (
            <div className="muted">
              {connection.name}
              {connection.domain ? ` · ${connection.domain}` : ""}{" "}
              {connection.status === "failed" ? (
                <span className="pill pillWarn" title={connection.error ?? undefined}>
                  Needs attention
                </span>
              ) : connection.status === "success" ? (
                <span className="pill pillOk">Healthy</span>
              ) : null}
            </div>
          ) : null}
          {connection?.status === "failed" && connection.error ? (
            <div className="help" style={{ color: "var(--danger-text)" }}>{connection.error}</div>
          ) : null}
          <div className="muted">{rollupLine(data?.rollup ?? {})}</div>
          <div className="muted" style={{ fontSize: 12 }}>
            Documents mirror within a few minutes of landing. Turning sync off leaves existing copies where they are.
          </div>
        </div>
      ) : null}

      {props.editable ? (
        <>
          <label className="field">
            <span className="label">{props.scope === "personal" ? "OneDrive folder" : "SharePoint location"}</span>
            <input
              className="input"
              value={targetRoot}
              onChange={(e) => setTargetRoot(e.target.value)}
              placeholder={placeholder}
              disabled={busy}
            />
            <div className="help">{help}</div>
            {fieldError ? <div className="help" style={{ color: "var(--danger-text)" }}>{fieldError}</div> : null}
          </label>
          <div className="actions">
            <button
              type="button"
              className="btn btnPrimary"
              onClick={() => void save()}
              disabled={busy || targetRoot.trim() === "" || (!dirty && policy != null)}
            >
              {busy ? "Saving…" : policy ? "Save" : "Turn on sync"}
            </button>
            {policy ? (
              <>
                <button type="button" className="btn" onClick={() => void toggleEnabled()} disabled={busy}>
                  {policy.enabled ? "Turn off" : "Turn on"}
                </button>
                <button type="button" className="btnTertiary" onClick={() => void remove()} disabled={busy}>
                  Remove
                </button>
              </>
            ) : null}
          </div>
        </>
      ) : policy == null ? (
        <div className="muted" style={{ fontSize: 13 }}>No sync configured.</div>
      ) : null}
    </div>
  );
}
